import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Stage } from '../services/stagesApi';
import { LeadWithAgent } from '../services/leadsApi';
import { User, Phone } from 'lucide-react';

interface FunnelColumnProps {
  stage: Stage;
  leads: LeadWithAgent[];
  onLeadUpdated?: () => void;
}

const FunnelColumn: React.FC<FunnelColumnProps> = ({ stage, leads }) => {
  const navigate = useNavigate();

  const handleCardClick = (lead: LeadWithAgent) => {
    // Navigate to Conversaciones with this lead pre-selected
    const params = new URLSearchParams();
    params.set('leadId', lead.id);
    if (lead.assigned_agent_id) params.set('agentId', lead.assigned_agent_id);
    navigate(`/inbox?${params.toString()}`);
  };

  return (
    <div className="w-[260px] flex-shrink-0 bg-white rounded-xl border border-grey-200 flex flex-col h-full">
      {/* Stage header */}
      <div className="px-4 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <span className="font-semibold text-sm text-foreground font-montserrat truncate">
            {stage.name}
          </span>
          <span className="text-xs text-grey-500 bg-grey-100 px-2 py-0.5 rounded-full font-medium">
            {leads.length}
          </span>
        </div>
        <div
          className="w-full h-[3px] rounded-full"
          style={{ backgroundColor: stage.color || '#6366F1' }}
        />
      </div>

      {/* Leads - scrollable */}
      <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-2">
        {leads.length === 0 ? (
          <p className="text-xs text-grey-400 text-center py-6">
            Sin leads
          </p>
        ) : (
          leads.map((lead) => (
            <FunnelLeadCard key={lead.id} lead={lead} onClick={() => handleCardClick(lead)} />
          ))
        )}
      </div>
    </div>
  );
};

// Compact lead card for funnel view
const FunnelLeadCard: React.FC<{ lead: LeadWithAgent; onClick: () => void }> = ({
  lead,
  onClick,
}) => {
  const hasName = lead.name && lead.name.trim() !== '';
  const displayName = hasName ? lead.name : lead.telefono_lead || 'Sin nombre';
  const formattedDate = lead.updated_at
    ? new Date(lead.updated_at).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
      })
    : '';

  return (
    <div
      onClick={onClick}
      className="p-3 bg-grey-50 hover:bg-grey-100 rounded-lg cursor-pointer transition-colors border border-grey-100 hover:border-grey-200"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-7 h-7 bg-white rounded-full flex items-center justify-center flex-shrink-0 border border-grey-200">
          <User className="w-3.5 h-3.5 text-grey-500" />
        </div>
        <span className="font-medium text-sm text-foreground truncate flex-1">{displayName}</span>
      </div>

      {hasName && lead.telefono_lead && (
        <div className="flex items-center gap-1 text-xs text-grey-500 mb-1 ml-9">
          <Phone className="w-3 h-3" />
          <span className="font-mono text-[11px]">{lead.telefono_lead}</span>
        </div>
      )}

      <div className="flex items-center justify-between ml-9">
        <span className="text-[11px] text-grey-400 truncate">
          {lead.agent_name || 'Sin agente'}
        </span>
        {formattedDate && (
          <span className="text-[11px] text-grey-400 flex-shrink-0">{formattedDate}</span>
        )}
      </div>
    </div>
  );
};

export default FunnelColumn;
